class VehicleModelSelector extends HTMLElement {
  constructor() {
    super();
    // console.log('VehicleModelSelector initialized');
    this.mql = window.matchMedia('(min-width: 990px)');
    this.elements = {
      vehicleButton: this.querySelector('.disclosure__button'),
      vehiclePanel: this.querySelector('.disclosure__list-wrapper'),
      vehicleListScroll: this.querySelector('.disclosure__list-scroll'),
      search: this.querySelector('input[name="vehicle_filter"]'),
      resetButton: this.querySelector('.vehicle-filter .reset__button'),
      closeButton: this.querySelector('.vehicle-filter__reset-button'),
      clearButton: this.querySelector('.clear__button'),
      vehicleOptions: this.querySelectorAll('#vehicle-model-list .disclosure__item button'),
      generationSelector: this.querySelector('.generation-selector'),
      generationList: this.querySelector('#generation-list'),
    };
    this.pendingVehicleOption = null;
    this.vehiclePointerStartY = 0;
    this.vehiclePointerHandled = false;
    this.onVehiclePanelPointerDown = this.onVehiclePanelPointerDown.bind(this);
    this.onVehiclePanelPointerUp = this.onVehiclePanelPointerUp.bind(this);
    this.onVehiclePanelPointerCancel = this.onVehiclePanelPointerCancel.bind(this);
    this.addEventListeners();
    this.syncVehicleStickyOffset = this.syncVehicleStickyOffset.bind(this);
    window.addEventListener('resize', this.syncVehicleStickyOffset);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.syncVehicleStickyOffset);
  }

  addEventListeners() {
    this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.elements.vehicleButton.addEventListener('click', this.toggleSelector.bind(this, 'vehicle'));

    this.elements.vehiclePanel.addEventListener('pointerdown', this.onVehiclePanelPointerDown);
    this.elements.vehiclePanel.addEventListener('pointerup', this.onVehiclePanelPointerUp);
    this.elements.vehiclePanel.addEventListener('pointercancel', this.onVehiclePanelPointerCancel);
    this.elements.vehiclePanel.addEventListener('click', (event) => {
      const option = event.target.closest('button.disclosure__link');
      if (!option || !this.elements.vehiclePanel.contains(option)) return;
      if (this.vehiclePointerHandled) return;
      this.onVehicleItemClick(event);
    });

    this.elements.generationList?.addEventListener('click', (event) => {
      const option = event.target.closest('[role="radio"]');
      if (!option || !this.elements.generationList.contains(option)) return;
      this.selectGeneration(option.dataset.generation);
    });

    this.elements.generationList?.addEventListener('keydown', (event) => {
      this.onGenerationKeyDown(event);
    });

    if (this.elements.search) {
      this.elements.search.addEventListener('input', this.onSearchInput.bind(this));
      this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
    }

    if (this.elements.resetButton) {
      this.elements.resetButton.addEventListener('pointerdown', (event) => event.preventDefault());
      this.elements.resetButton.addEventListener('click', this.onResetSearch.bind(this));
    }

    if (this.elements.closeButton) {
      this.elements.closeButton.addEventListener('click', this.closeModal.bind(this));
    }

    if (this.elements.clearButton) {
      this.elements.clearButton.addEventListener('pointerdown', (event) => event.preventDefault());
      this.elements.clearButton.addEventListener('click', this.onClearSelection.bind(this));
    }

    document.addEventListener('click', this.handleOutsideClick.bind(this));
    this.toggleClearButton();
    this.toggleResetButton();
  }

  syncVehicleStickyOffset() {
    const scrollEl = this.elements.vehicleListScroll;
    const panel = this.elements.vehiclePanel;
    if (!scrollEl || !panel) return;

    const filter = panel.querySelector('.vehicle-filter');
    const filterHeight = filter ? filter.offsetHeight : 0;
    scrollEl.style.maxHeight = filterHeight
      ? `calc(27.5rem - ${filterHeight}px)`
      : '27.5rem';
  }

  showPanel(type) {
    const button = type === 'vehicle' ? this.elements.vehicleButton : this.elements.generationButton;
    const panel = type === 'vehicle' ? this.elements.vehiclePanel : this.elements.generationPanel;
    if (!button || !panel) return;

    DisclosurePanel.show(panel, button);

    if (type === 'vehicle') {
      requestAnimationFrame(() => this.syncVehicleStickyOffset());
    }
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.hidePanel('vehicle');
      this.hidePanel('generation');
    }
  }

  isPanelOpen(type) {
    const button = type === 'generation' ? this.elements.generationButton : this.elements.vehicleButton;
    return button && button.getAttribute('aria-expanded') === 'true';
  }

  getVisibleOptions(type) {
    const panel = type === 'generation' ? this.elements.generationPanel : this.elements.vehiclePanel;
    if (!panel || panel.hasAttribute('hidden')) return [];
    return Array.from(panel.querySelectorAll('button.disclosure__link')).filter((item) => {
      if (item.hidden || item.getAttribute('aria-hidden') === 'true') return false;
      const itemRow = item.closest('.disclosure__item');
      if (itemRow && (itemRow.hidden || itemRow.style.display === 'none')) return false;
      return true;
    });
  }

  focusOption(type, which) {
    const options = this.getVisibleOptions(type);
    if (!options.length) return;
    const option = which === 'last' ? options[options.length - 1] : options[0];
    option.focus();
  }

  moveOptionFocus(type, key) {
    const options = this.getVisibleOptions(type);
    if (!options.length) return;

    let index = options.findIndex((item) => item === document.activeElement);
    if (key === 'Home') index = 0;
    else if (key === 'End') index = options.length - 1;
    else if (key === 'ArrowUp') index = index > 0 ? index - 1 : options.length - 1;
    else index = index < options.length - 1 ? index + 1 : 0;

    options[index].focus();
  }

  selectFocusedOption(type) {
    const options = this.getVisibleOptions(type);
    const focused = options.find((item) => item === document.activeElement);
    const option = focused || options[0];
    if (!option) return;
    option.click();
  }

  onVehiclePanelPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const option = event.target.closest('button.disclosure__link');
    if (!option || !this.elements.vehiclePanel.contains(option)) {
      this.pendingVehicleOption = null;
      return;
    }
    this.pendingVehicleOption = option;
    this.vehiclePointerStartY = event.clientY;
  }

  onVehiclePanelPointerUp(event) {
    const option = this.pendingVehicleOption;
    this.pendingVehicleOption = null;
    if (!option || !this.elements.vehiclePanel.contains(option)) return;
    if (Math.abs(event.clientY - this.vehiclePointerStartY) > 12) return;

    this.vehiclePointerHandled = true;
    this.onVehicleItemClick({ target: option, suppressFocus: event.pointerType !== 'mouse' });
    setTimeout(() => {
      this.vehiclePointerHandled = false;
    }, 400);
  }

  onVehiclePanelPointerCancel() {
    this.pendingVehicleOption = null;
    this.vehiclePointerHandled = false;
  }

  onFocusOut(event) {
    if (this.contains(event.relatedTarget) || this.pendingVehicleOption) return;
    requestAnimationFrame(() => {
      if (this.contains(document.activeElement) || this.pendingVehicleOption) return;
      this.hidePanel('vehicle');
      this.hidePanel('generation');
    });
  }

  onContainerKeyDown(event) {
    const key = event.key;
    const isSearch = event.target === this.elements.search;
    const isVehicleTrigger = event.target === this.elements.vehicleButton;
    const isGenerationTrigger = event.target === this.elements.generationButton;
    const triggerType = isGenerationTrigger ? 'generation' : isVehicleTrigger ? 'vehicle' : null;
    const openType = this.isPanelOpen('generation') ? 'generation' : this.isPanelOpen('vehicle') ? 'vehicle' : null;

    if (key === 'Escape') {
      if (!openType) return;
      event.preventDefault();
      event.stopPropagation();
      this.hidePanel(openType);
      const button = openType === 'generation' ? this.elements.generationButton : this.elements.vehicleButton;
      button?.focus();
      return;
    }

    if (key === 'Enter' || key === ' ') {
      if (isSearch && key === ' ') return;
      if (triggerType) {
        event.preventDefault();
        if (!this.isPanelOpen(triggerType)) this.toggleSelector(triggerType, event);
        return;
      }
      if (!openType) return;
      event.preventDefault();
      this.selectFocusedOption(openType);
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return;

    if (triggerType && (key === 'ArrowDown' || key === 'ArrowUp')) {
      event.preventDefault();
      if (!this.isPanelOpen(triggerType)) this.toggleSelector(triggerType, event);
      requestAnimationFrame(() => this.focusOption(triggerType, key === 'ArrowUp' ? 'last' : 'first'));
      return;
    }

    if (isSearch && (key === 'ArrowDown' || key === 'ArrowUp')) {
      event.preventDefault();
      this.moveOptionFocus('vehicle', key);
      return;
    }

    if (!openType) return;
    event.preventDefault();
    this.moveOptionFocus(openType, key);
  }

  toggleSelector(type, event) {
    if (this.vehiclePointerHandled) return;
    if (event) {
      event.stopPropagation();
    }
    const button = type === 'vehicle' ? this.elements.vehicleButton : this.elements.generationButton;
    const isExpanded = button.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.hidePanel(type);
      return;
    }

    DisclosurePanel.closeSiblings(
      type === 'vehicle' ? this.elements.vehiclePanel : this.elements.generationPanel,
      this
    );
    this.showPanel(type);

    const openedByPointer = event && typeof event.detail === 'number' && event.detail > 0;
    if (type === 'vehicle' && this.elements.search && this.mql.matches && openedByPointer) {
      this.elements.search.focus();
    }
  }

  getOptionSearchText(optionLink) {
    return (optionLink.dataset.optionText || optionLink.textContent || '').toLowerCase().trim();
  }

  updateVehicleTrigger(optionButton) {
    const valueSpan = this.elements.vehicleButton.querySelector('span');
    const vehicleMarkup = optionButton.querySelector('.cart-item-notes__vehicle');
    const optionText = (optionButton.dataset.optionText || optionButton.textContent || '').trim();

    valueSpan.innerHTML = '';
    if (vehicleMarkup) {
      valueSpan.appendChild(vehicleMarkup.cloneNode(true));
    } else {
      valueSpan.textContent = optionText;
    }

    if (optionText) {
      valueSpan.dataset.optionText = optionText;
    } else {
      delete valueSpan.dataset.optionText;
    }

    if (optionButton.dataset.vehicleOdooSku) {
      valueSpan.setAttribute('data-vehicle-odoo-sku', optionButton.dataset.vehicleOdooSku);
    } else {
      valueSpan.removeAttribute('data-vehicle-odoo-sku');
    }
  }

  syncVehicleGroupsVisibility() {
    this.querySelectorAll('.disclosure__group').forEach((group) => {
      const items = group.querySelectorAll('.disclosure__item');
      const hasVisible = Array.from(items).some((item) => item.style.display !== 'none');
      group.style.display = hasVisible ? '' : 'none';
    });
  }

  filterOptions() {
    const searchValue = this.elements.search?.value.toLowerCase().trim() || '';
    const searchRegex = searchValue ? new RegExp(searchValue, 'i') : null;
    const options = this.querySelectorAll('.disclosure__item');

    options.forEach((option) => {
      const optionLink = option.querySelector('button.disclosure__link');
      const optionText = this.getOptionSearchText(optionLink);
      const isMatch = !searchRegex || searchRegex.test(optionText);
      option.style.display = isMatch ? '' : 'none';
    });

        this.syncVehicleGroupsVisibility();
  }

  onSearchInput() {
    this.filterOptions();
    this.toggleResetButton();
  }

  toggleResetButton() {
    const button = this.elements.resetButton;
    if (!button) return;
    const hasValue = !!(this.elements.search && this.elements.search.value);
    button.hidden = !hasValue;
    button.classList.toggle('hidden', !hasValue);
  }

  onResetSearch(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.elements.search) return;
    this.elements.search.value = '';
    this.filterOptions();
    this.toggleResetButton();
    this.elements.search.focus();
  }

  closeModal(event) {
    event.preventDefault();
    event.stopPropagation();
    this.hidePanel('vehicle');
    this.hidePanel('generation');
  }

  hidePanel(type) {
    const button = type === 'vehicle' ? this.elements.vehicleButton : this.elements.generationButton;
    const panel = type === 'vehicle' ? this.elements.vehiclePanel : this.elements.generationPanel;
    if (!button || !panel) return;

    DisclosurePanel.hide(panel, button);
  }

  onSearchKeyDown(event) {
    if (event.code.toUpperCase() === 'ENTER') {
      event.preventDefault();
    }
  }

  onVehicleItemClick(event) {
    const target = event.target.closest('button.disclosure__link') || event.target;
    const selectedValue = target.dataset.value;
    const selectedVehicleSize = target.dataset.vehicleSize;
    const generationData = target.dataset.generation;

    // Clear previous selections
    this.querySelectorAll('.disclosure__link[aria-selected="true"]').forEach((button) => {
      button.setAttribute('aria-selected', 'false');
      button.classList.remove('selected');
    });

    // Set current selection
    target.setAttribute('aria-selected', 'true');
    target.classList.add('selected');

    this.updateVehicleTrigger(target);
    this.toggleClearButton();
    if (this.elements.search) {
      this.elements.search.value = '';
      this.filterOptions();
      this.toggleResetButton();
    }
    this.hidePanel('vehicle');
    if (!event.suppressFocus) {
      this.elements.vehicleButton.focus();
    }

    // console.log('Vehicle item clicked:', { selectedValue, selectedVehicleSize, generationData });

    // Update Shopify's variant selector (radio buttons)
    const variantRadios = document.querySelectorAll('input[type="radio"]');
    const matchingVariant = Array.from(variantRadios).find(
      (radio) => this.handleize(radio.value) === this.handleize(selectedVehicleSize)
    );

    if (matchingVariant) {
      matchingVariant.checked = true;
      matchingVariant.dispatchEvent(new Event('change', { bubbles: true }));
      // console.log('Updated variant selection:', matchingVariant.value);
    } else {
      console.warn('Matching variant radio button not found');
    }

    // Handle generation data
    if (generationData) {
      this.populateGenerationDropdown(generationData);
    } else {
      this.hideGenerationDropdown();
    }

    // Dispatch a custom event for vehicle selection
    this.dispatchEvent(
      new CustomEvent('vehicleSelected', {
        bubbles: true,
        detail: {
          value: selectedValue,
          label: (target.dataset.optionText || target.textContent || '').trim(),
          vehicleSize: selectedVehicleSize,
          generation: generationData ? '' : undefined,
        },
      })
    );
  }

  parseGenerations(generationData) {
    return (generationData || '')
      .split(/,|\band\b/i)
      .map((gen) =>
        gen
          .replace(/^and\s+/i, '')
          .replace(/\s+/g, ' ')
          .trim()
      )
      .filter(Boolean);
  }

  getGenerationOptions() {
    return Array.from(this.elements.generationList?.querySelectorAll('[role="radio"]') || []);
  }

  getSelectedGeneration() {
    const selected = this.elements.generationList?.querySelector('[role="radio"][aria-checked="true"]');
    return selected ? (selected.dataset.generation || '').trim() : '';
  }

  selectGeneration(generation, options = {}) {
    const value = (generation || '').trim();
    const radios = this.getGenerationOptions();
    let focused = null;

    radios.forEach((radio) => {
      const isMatch = (radio.dataset.generation || '').trim() === value;
      radio.setAttribute('aria-checked', isMatch ? 'true' : 'false');
      radio.tabIndex = isMatch ? 0 : -1;
      if (isMatch) focused = radio;
    });

    if (!focused && radios[0]) radios[0].tabIndex = 0;

    if (options.silent) return;
    if (options.focus && focused) focused.focus();

    this.dispatchEvent(
      new CustomEvent('generationSelected', {
        bubbles: true,
        detail: { generation: value },
      })
    );
  }

  onGenerationKeyDown(event) {
    const radios = this.getGenerationOptions();
    if (!radios.length) return;

    const current = event.target.closest('[role="radio"]');
    if (!current || !this.elements.generationList.contains(current)) return;

    const key = event.key;
    const currentIndex = radios.indexOf(current);
    let nextIndex = currentIndex;

    if (key === 'ArrowRight' || key === 'ArrowDown') nextIndex = (currentIndex + 1) % radios.length;
    else if (key === 'ArrowLeft' || key === 'ArrowUp') nextIndex = (currentIndex - 1 + radios.length) % radios.length;
    else if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = radios.length - 1;
    else if (key === ' ' || key === 'Enter') {
      event.preventDefault();
      this.selectGeneration(current.dataset.generation, { focus: true });
      return;
    } else {
      return;
    }

    event.preventDefault();
    this.selectGeneration(radios[nextIndex].dataset.generation, { focus: true });
  }

  populateGenerationDropdown(generationData) {
    const generations = this.parseGenerations(generationData);

    if (!this.elements.generationSelector || !this.elements.generationList) return;

    this.elements.generationList.innerHTML = '';
    this.elements.generationList.setAttribute('aria-required', generations.length > 1 ? 'true' : 'false');

    generations.forEach((gen, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'generation-pill';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.tabIndex = index === 0 ? 0 : -1;
      button.dataset.generation = gen;
      button.textContent = gen;
      this.elements.generationList.appendChild(button);
    });

    if (generations.length === 1) {
      this.elements.generationSelector.hidden = true;
      this.elements.generationSelector.style.display = 'none';
      this.selectGeneration(generations[0]);
      return;
    }

    this.elements.generationSelector.hidden = false;
    this.elements.generationSelector.style.display = '';
    this.selectGeneration('', { silent: true });
    this.dispatchEvent(
      new CustomEvent('generationSelected', {
        bubbles: true,
        detail: { generation: '' },
      })
    );
  }

  hideGenerationDropdown() {
    if (this.elements.generationSelector) {
      this.elements.generationSelector.hidden = true;
      this.elements.generationSelector.style.display = 'none';
    }
    if (this.elements.generationList) this.elements.generationList.innerHTML = '';
    this.dispatchEvent(
      new CustomEvent('generationSelected', {
        bubbles: true,
        detail: { generation: '' },
      })
    );
  }

  toggleClearButton() {
    const button = this.elements.clearButton;
    if (!button) return;
    const hasSelection = !!this.querySelector('.disclosure__link[aria-selected="true"]');
    button.hidden = !hasSelection;
    button.classList.toggle('hidden', !hasSelection);
  }

  resetVehicleTrigger() {
    const valueSpan =
      this.elements.vehicleButton?.querySelector('#vehicle-model-value') ||
      this.elements.vehicleButton?.querySelector('span[data-label]');
    if (!valueSpan) return;

    const placeholder =
      valueSpan.getAttribute('data-label') || this.elements.vehicleButton.getAttribute('data-label') || '';
    valueSpan.innerHTML = '';
    valueSpan.textContent = placeholder;
    delete valueSpan.dataset.optionText;
    valueSpan.removeAttribute('data-vehicle-odoo-sku');
  }

  onClearSelection(event) {
    event.preventDefault();
    event.stopPropagation();

    this.querySelectorAll('.disclosure__link[aria-selected="true"]').forEach((button) => {
      button.setAttribute('aria-selected', 'false');
      button.classList.remove('selected');
    });

    this.resetVehicleTrigger();
    this.hideGenerationDropdown();
    this.hidePanel('vehicle');
    this.toggleClearButton();

    this.dispatchEvent(
      new CustomEvent('vehicleSelected', {
        bubbles: true,
        detail: { value: '', label: '', vehicleSize: '' },
      })
    );
  }

  handleize(str) {
    return String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '');
  }

  onModelSelect(event) {
    const button = event.currentTarget;
    const selectedSize = button.getAttribute('data-vehicle-size');

    // Find and select the matching size radio button
    const sizeRadioButton = document.querySelector(`input[name^="Size"][value="${selectedSize}"]`);
    if (sizeRadioButton) {
      sizeRadioButton.checked = true;
    } else {
      console.error('Matching variant radio button not found');
    }
  }
}

customElements.define('vehicle-model-selector', VehicleModelSelector);
